import request from 'utils/request'
import { combineReducers } from 'redux'
import { downloadBlob } from 'utils/misc'
import { formatEventBody } from 'utils/events'
import {
  LOCAL_STORAGE_KEYS
  , RESPONSE_STATES
} from 'utils/constants'

import {
  addCompletionDate,
  checkAndAddCompletedConsentQuestion,
  dedupeCompletedConsentsByLatestVersion,
  normalizeQuestionStatesForSubmission
} from '../utils/consentUtils'
import { logSentryEvent, SENTRY_MESSAGES } from '../../../utils/sentry'

// Same string as Login.js; kept local to avoid Consent ↔ Login circular import.
const SET_TEMP_PASSWORD = 'SET_TEMP_PASSWORD'

const SET_CONSENT = 'SET_CONSENT'
const SET_SUBMISSION_CONSENT = 'SET_SUBMISSION_CONSENT'
const ADD_COMPLETED_CONSENT_QUESTION = 'ADD_COMPLETED_CONSENT_QUESTION'
const SET_SKIPPED_CONSENT_QUESTION = 'SET_SKIPPED_CONSENT_QUESTION'
const SET_CONSENT_ERROR = 'SET_CONSENT_ERROR'
const CLEAR_CONSENT_STATUS = 'CLEAR_CONSENT_STATUS'
const SET_HAS_CONSENTED = 'SET_HAS_CONSENTED'
const SET_V2_CONSENTS = 'SET_V2_CONSENTS'
const SET_V2_COMPLETED_CONSENTS = 'SET_V2_COMPLETED_CONSENTS'
const SET_COMPLETED_CONSENTS = 'SET_COMPLETED_CONSENTS'
const USER_CONSENTED = 'USER_CONSENTED'
const USER_LOGGED_OUT = 'USER_LOGGED_OUT'
const SET_USER_DATA = 'SET_USER_DATA'

const setConsent = consent => {
  return {
    type: SET_CONSENT,
    consent
  }
}

const setConsentError = payload => {
  return {
    type: SET_CONSENT_ERROR,
    payload
  }
}

const setV2Consents = consents => {
  return {
    type: SET_V2_CONSENTS,
    consents
  }
}

const setV2CompletedConsents = consents => {
  return {
    type: SET_V2_COMPLETED_CONSENTS,
    consents
  }
}

export const setCompletedConsents = payload => {
  const { consents } = payload
  return {
    type: SET_COMPLETED_CONSENTS,
    consents: consents.reverse()
  }
}

export const clearConsentStatus = () => {
  return {
    type: CLEAR_CONSENT_STATUS
  }
}

export const setHasConsented = () => {
  return {
    type: SET_HAS_CONSENTED
  }
}

// Per-consent in-flight guard to prevent duplicate download for the same consent (e.g. from multiple list responses)
let consentDetailsInFlightKey = null

export const onFetchConsentDetails = (consentId, consentVersion) => {
  return dispatch => {
    const key = `${consentId}_${consentVersion}`
    if (consentDetailsInFlightKey === key) {
      return Promise.resolve()
    }
    consentDetailsInFlightKey = key
    logSentryEvent({
      message: SENTRY_MESSAGES.consentStartDownload,
      tags: {
        flow: 'consent',
        action: 'start_download',
        consent_id: consentId != null ? String(consentId) : undefined,
        consent_version: consentVersion != null ? String(consentVersion) : undefined,
      },
    })
    return dispatch(
      request({
        method: 'GET',
        url: `/control/v2/participants/me/consent/download?consent_id=${consentId}&version=${consentVersion}`,
        hasLoader: true,
        success: json => {
          logSentryEvent({
            message: SENTRY_MESSAGES.consentDownloaded,
            tags: {
              flow: 'consent',
              action: 'downloaded',
              consent_id: String(json?.consent_id || consentId || ''),
              consent_name: json?.consent_title || json?.title,
              consent_version: String(
                json?.consent_version || json?.last_deployed_version || consentVersion || '',
              ),
            },
          })
          dispatch(setConsent(json))
        },
        onFailLog: () => {
          logSentryEvent({
            message: SENTRY_MESSAGES.consentDownloadFailed,
            level: 'error',
            tags: {
              flow: 'consent',
              action: 'download_failed',
              consent_id: consentId != null ? String(consentId) : undefined,
              consent_version: consentVersion != null ? String(consentVersion) : undefined,
            },
          })
        },
      })
    ).finally(() => {
      if (consentDetailsInFlightKey === key) {
        consentDetailsInFlightKey = null
      }
    })
  }
}

// In-flight guard and short cache to prevent duplicate consent list requests when multiple routes dispatch
let consentListRequestInFlight = false
const CONSENT_LIST_CACHE_MS = 4000
let consentListLastFetchedAt = 0

export const onFetchConsent = (studyID, options = {}) => {
  const { skipLoader = false, forceRefresh = false } = options
  return (dispatch, getState) => {
    if (consentListRequestInFlight) {
      return Promise.resolve()
    }
    const state = getState()
    const hasCachedList = (state.consent?.v2Consents?.length ?? 0) > 0
    const cacheFresh = Date.now() - consentListLastFetchedAt < CONSENT_LIST_CACHE_MS
    if (!forceRefresh && hasCachedList && cacheFresh) {
      return Promise.resolve()
    }
    consentListRequestInFlight = true
    return dispatch(
      request({
        method: 'GET',
        url: '/control/v2/participants/me/consent?filter=all',
        hasLoader: !skipLoader,
        success: json => {
          consentListLastFetchedAt = Date.now()
          if (!json?.consents?.length) {
            const currentUser = localStorage.getItem(LOCAL_STORAGE_KEYS.user)
            const updatedUser = {
              ...JSON.parse(currentUser),
              has_consented: true
            };
            localStorage.setItem(LOCAL_STORAGE_KEYS.user, JSON.stringify(updatedUser));

            // Update Redux user state to reflect has_consented: true
            // This ensures UserHasConsented wrapper allows access to protected routes
            dispatch({
              type: SET_USER_DATA,
              payload: updatedUser
            })

            if (studyID) {
              dispatch(onConsentAccept(studyID))
            }

            dispatch(clearConsentStatus())
            dispatch(setHasConsented())
            // Note: Redirect is handled by ConsentContainer to avoid infinite loops
            // since onFetchConsent is called from multiple routes (activities, visits, etc.)
            return
          }
          dispatch(setV2Consents(json?.consents))

          // Pre-fetch full consent content for the first consent when there are any pending consents.
          // This ensures consentForm (and is_blocking) is set so the blocking consent modal can show
          // on Activities/Visits after refresh (GOLD-11140). When multiple consents exist, the user
          // can still pick a different one from Profile (GOLD-11364).
          // Skip download if we already have this consent's form loaded to avoid duplicate API calls.
          if (json?.consents?.length >= 1) {
            const data = json.consents[0]
            const state = getState()
            const existingConsentId = state.consent?.consentForm?.consent_id
            if (existingConsentId === data?.consent_id) {
              return
            }
            dispatch(
              onFetchConsentDetails(
                data?.consent_id,
                data?.last_deployed_version
              )
            )
          }
        },
        onFailLog: () => {
          logSentryEvent({
            message: SENTRY_MESSAGES.pendingConsentsFetchFailed,
            level: 'error',
            tags: {
              flow: 'consent',
              action: 'pending_fetch_failed',
            },
          })
        },
      })
    ).finally(() => {
      consentListRequestInFlight = false
    })
  }
}

// In-flight guard to prevent duplicate completed-consent requests (Profile + Consent route)
let completedConsentRequestInFlight = false

export const onFetchCompletedConsent = studyID => {
  return (dispatch, getState) => {
    if (completedConsentRequestInFlight) {
      return Promise.resolve()
    }
    completedConsentRequestInFlight = true
    return dispatch(
      request({
        method: 'GET',
        url: '/control/v2/participants/me/completed/consent',
        hasLoader: true,
        success: json => {
          if (!json?.consents?.length) {
          } else {
            dispatch(setV2CompletedConsents(json?.consents))
          }
          logSentryEvent({
            message: SENTRY_MESSAGES.completedConsentsListDelivered,
            tags: {
              flow: 'consent',
              action: 'completed_list_delivered',
              count: json?.consents?.length || 0,
            },
          })
          ;(json?.consents || []).forEach(consent => {
            logSentryEvent({
              message: SENTRY_MESSAGES.completedConsentDelivered,
              tags: {
                flow: 'consent',
                action: 'completed_consent_delivered',
                consent_id: consent?.consent_id != null ? String(consent.consent_id) : undefined,
                consent_name: consent?.consent_title || consent?.title || consent?.name,
              },
            })
          })
        },
        onFailLog: () => {
          logSentryEvent({
            message: SENTRY_MESSAGES.completedConsentsListFetchFailed,
            level: 'error',
            tags: {
              flow: 'consent',
              action: 'completed_list_fetch_failed',
            },
          })
        },
      })
    ).finally(() => {
      completedConsentRequestInFlight = false
    })
  }
}

export const completeConsentQuestion = (id, data) => {
  return {
    type: ADD_COMPLETED_CONSENT_QUESTION,
    id,
    data
  }
}

export const skipConsentQuestion = id => {
  return {
    type: SET_SKIPPED_CONSENT_QUESTION,
    id
  }
}

const setSubmissionConsent = consent => {
  return {
    type: SET_SUBMISSION_CONSENT,
    consent
  }
}

export const onSetSubmissionConsent = consent => {
  return dispatch => {
    dispatch(setSubmissionConsent(consent))
  }
}

export const endConsent = () => {
  return {
    type: USER_CONSENTED
  }
}

export const onConsentOpen = (studyID, consentId, version, consentName) => {
  return dispatch => {
    logSentryEvent({
      message: SENTRY_MESSAGES.consentOpened,
      tags: {
        flow: 'consent',
        action: 'opened',
        consent_id: consentId != null ? String(consentId) : undefined,
        consent_name: consentName,
        consent_version: version != null ? String(version) : undefined,
      },
    })
    const additionalInfo = {}
    if (consentId !== undefined && consentId !== null) {
      additionalInfo.consent_id = consentId
    }
    if (version !== undefined && version !== null) {
      additionalInfo.version = version
    }
    return dispatch(
      request({
        method: 'POST',
        url: '/control/participants/me/events',
        body: formatEventBody('CONSENT_OPENED', 'study_id', studyID, additionalInfo),
        success: () => { }
      })
    )
  }
}

export const onConsentAccept = studyID => {
  return dispatch => {
    logSentryEvent({
      message: SENTRY_MESSAGES.consentAccepted,
      tags: {
        flow: 'consent',
        action: 'accepted',
      },
    })
    return dispatch(
      request({
        method: 'POST',
        url: '/control/participants/me/events',
        body: formatEventBody('CONSENT_ACCEPTED', 'study_id', studyID),
        success: () => { }
      })
    )
  }
}

export const onConsentReject = (studyID, consentId, version) => {
  return dispatch => {
    logSentryEvent({
      message: SENTRY_MESSAGES.consentRejected,
      tags: {
        flow: 'consent',
        action: 'rejected',
        consent_id: consentId != null ? String(consentId) : undefined,
        consent_version: version != null ? String(version) : undefined,
      },
    })
    const additionalInfo = {}
    if (consentId !== undefined && consentId !== null) {
      additionalInfo.consent_id = consentId
    }
    if (version !== undefined && version !== null) {
      additionalInfo.version = version
    }
    return dispatch(
      request({
        method: 'POST',
        url: '/control/participants/me/events',
        body: formatEventBody('CONSENT_REJECTED', 'study_id', studyID, additionalInfo),
        success: () => { }
      })
    )
  }
}

export const submitConsent = (consent, studyID, errorMessage) => {
  return dispatch => {
    logSentryEvent({
      message: SENTRY_MESSAGES.consentStartSubmit,
      tags: {
        flow: 'consent',
        action: 'api_submit_started',
        consent_id: consent?.consent_id != null ? String(consent.consent_id) : undefined,
        consent_name: consent?.consent_title,
      },
    })
    const normalized = normalizeQuestionStatesForSubmission(consent)
    const toSubmit = addCompletionDate(normalized)

    const success = () => {
      logSentryEvent({
        message: SENTRY_MESSAGES.consentSubmitted,
        tags: {
          flow: 'consent',
          action: 'api_submit_success',
          consent_id: toSubmit?.consent_id != null ? String(toSubmit.consent_id) : undefined,
        },
      })
      dispatch((dispatch, getState) => {
        const state = getState()
        const consentId = toSubmit.consent_id
        const pendingConsents = state.consent.v2Consents || []
        const completedConsent = pendingConsents.find(c => c.consent_id === consentId)
        if (completedConsent) {
          const remainingPending = pendingConsents.filter(c => c.consent_id !== consentId)
          dispatch(setV2Consents(remainingPending))
          const updatedCompletedConsent = {
            ...completedConsent,
            completed: new Date().toISOString()
          }
          dispatch(setCompletedConsents({
            consents: [...(state.consent.completedConsents || []), updatedCompletedConsent]
          }))
        }

        // Always set hasConsented on successful submit so the UI shows the finish dialog
        // (ConsentCompletedView) immediately, including after comprehension-question submit.
        try {
          const currentUser = localStorage.getItem(LOCAL_STORAGE_KEYS.user)
          const updatedUser = {
            ...JSON.parse(currentUser || '{}'),
            has_consented: true
          }
          localStorage.setItem(LOCAL_STORAGE_KEYS.user, JSON.stringify(updatedUser))
          dispatch({ type: SET_USER_DATA, payload: updatedUser })
        } catch (_) { /* ignore */ }
        if (studyID) {
          dispatch(onConsentAccept(studyID))
        }
        dispatch(setHasConsented())
      })

      // Refresh from server without loader so ConsentPage does not unmount (which would reset didStartConsent)
      dispatch(onFetchConsent(studyID, { skipLoader: true }))
    }
    return dispatch(
      request({
        method: 'POST',
        url: '/control/v2/participants/me/consent',
        body: JSON.stringify(toSubmit),
        authRequired: false,
        success,
        skipNotySuccess: true,
        fail: () => setConsentError(errorMessage)
      })
    ).catch(error => {
      logSentryEvent({
        message: 'Consent Submit Failed',
        level: 'error',
        tags: {
          flow: 'consent',
          action: 'api_submit_failed',
          consent_id: consent?.consent_id != null ? String(consent.consent_id) : undefined,
        },
        error
      })
      throw error
    })
  }
}

// Expose a dedicated API wrapper for consent file uploads (multipart/form-data)
export const uploadConsentFile = formData => {
  return dispatch => {
    const success = res => res
    logSentryEvent({
      message: 'File upload: upload_started',
      tags: {
        flow: 'file-upload',
        'file.action': 'upload_started'
      },
      contexts: {
        fileUpload: {
          moduleName: 'consent',
          endpoint: '/control/v2/file/upload'
        }
      }
    })
    return dispatch(
      request({
        method: 'POST',
        url: '/control/v2/file/upload',
        contentType: 'multipart/form-data',
        body: formData,
        success,
        skipNotySuccess: true
      })
    ).then(response => {
      logSentryEvent({
        message: 'File upload: upload_success',
        tags: {
          flow: 'file-upload',
          'file.action': 'upload_success'
        },
        contexts: {
          fileUpload: {
            moduleName: 'consent',
            endpoint: '/control/v2/file/upload'
          }
        }
      })
      return response
    }).catch(error => {
      logSentryEvent({
        message: 'File upload: upload_failed',
        level: 'error',
        tags: {
          flow: 'file-upload',
          'file.action': 'upload_failed'
        },
        contexts: {
          fileUpload: {
            moduleName: 'consent',
            endpoint: '/control/v2/file/upload'
          }
        },
        error
      })
      throw error
    })
  }
}

export const downloadConsentPDF = (name, last_deployed_version, consent_id) => {

  return dispatch => {

    const success = blob => {
      logSentryEvent({
        message: SENTRY_MESSAGES.completedConsentPdfDownloaded,
        tags: {
          flow: 'consent',
          action: 'completed_pdf_downloaded',
          consent_id: consent_id != null ? String(consent_id) : undefined,
          consent_name: name,
          consent_version: last_deployed_version != null ? String(last_deployed_version) : undefined,
        },
      })
      downloadBlob(blob, `${name}-consent.pdf`)
    }
    return dispatch(
      request({
        method: 'GET',
        url: `control/v2/consent/pdf?consent_id=${consent_id}&version=${last_deployed_version}`,
        resType: 'blob',
        success,
        onFailLog: () => {
          logSentryEvent({
            message: SENTRY_MESSAGES.completedConsentPdfDownloadFailed,
            level: 'error',
            tags: {
              flow: 'consent',
              action: 'completed_pdf_download_failed',
              consent_id: consent_id != null ? String(consent_id) : undefined,
              consent_name: name,
            },
          })
        },
      })
    )
  }
}

export const downloadSignConsentPDF = (study_id, name, consent_id) => {

  return dispatch => {

    const success = blob => {
      logSentryEvent({
        message: SENTRY_MESSAGES.completedConsentPdfDownloaded,
        tags: {
          flow: 'consent',
          action: 'completed_pdf_downloaded',
          consent_id: consent_id != null ? String(consent_id) : undefined,
          consent_name: name,
        },
      })
      downloadBlob(blob, `${name}-consent.pdf`)
    }
    return dispatch(
      request({
        method: 'GET',
        url: `control/v2/studies/${study_id}/participants/me/download?consent_id=${consent_id}`,
        resType: 'blob',
        success,
        onFailLog: () => {
          logSentryEvent({
            message: SENTRY_MESSAGES.completedConsentPdfDownloadFailed,
            level: 'error',
            tags: {
              flow: 'consent',
              action: 'completed_pdf_download_failed',
              consent_id: consent_id != null ? String(consent_id) : undefined,
              consent_name: name,
            },
          })
        },
      })
    )
  }
}

const consentForm = (state = {}, action) => {
  switch (action.type) {
    case SET_CONSENT:
      return action.consent
    case USER_CONSENTED:
      // Clear consent form data when user completes consent
      // This prevents stale consent data from blocking navigation after completion
      return {}
    case USER_LOGGED_OUT:
      return {}
    default:
      return state
  }
}

const v2Consents = (state = [], action) => {
  switch (action.type) {
    case SET_V2_CONSENTS:
      return action.consents
    case USER_CONSENTED:
      // Clear pending consents list when user completes consent
      // This ensures no stale pending consents remain after completion
      return []
    case USER_LOGGED_OUT:
      return []
    default:
      return state
  }
}

const completedConsents = (state = [], action) => {
  switch (action.type) {
    case SET_V2_COMPLETED_CONSENTS:
      return dedupeCompletedConsentsByLatestVersion(action.consents)
    case SET_COMPLETED_CONSENTS:
      return dedupeCompletedConsentsByLatestVersion(action.consents)
    case USER_CONSENTED:
      // Clear completed consents when user completes consent
      return []
    case USER_LOGGED_OUT:
      return []
    default:
      return state
  }
}

const submissionConsent = (state = {}, action) => {
  switch (action.type) {
    case SET_SUBMISSION_CONSENT: {
      const newState = Object.assign({}, action.consent)
      return newState
    }
    case ADD_COMPLETED_CONSENT_QUESTION: {
      const newState = Object.assign({}, state)
      newState.questions = checkAndAddCompletedConsentQuestion(
        newState.questions,
        action.id,
        action.data
      )
      // Ensure state reflects answered when a signature is provided
      const answeredIdx = newState?.questions?.findIndex(q => q.id === action.id)
      if (answeredIdx > -1) {
        const updated = Object.assign({}, newState.questions[answeredIdx], {
          skipped: false,
          state: RESPONSE_STATES.answered
        })
        newState.questions = [
          ...newState.questions.slice(0, answeredIdx),
          updated,
          ...newState.questions.slice(answeredIdx + 1)
        ]
      }
      return newState
    }
    case SET_SKIPPED_CONSENT_QUESTION: {
      const newState = Object.assign({}, state)
      const qIndex = newState?.questions?.findIndex(q => q.id === action.id)
      if (qIndex > -1) {
        const updated = Object.assign({}, newState.questions[qIndex], {
          skipped: true,
          state: RESPONSE_STATES.skipped,
          answer: []
        })
        newState.questions = [
          ...newState.questions.slice(0, qIndex),
          updated,
          ...newState.questions.slice(qIndex + 1)
        ]
      }
      return newState
    }
    case USER_LOGGED_OUT:
      return {}
    default:
      return state
  }
}

const defaultStatus = {
  error: '',
  hasConsented: false
}

const consentStatus = (state = defaultStatus, action) => {
  let newState
  switch (action.type) {
    case SET_CONSENT_ERROR: {
      newState = Object.assign({}, state, { error: action.payload })
      return newState
    }
    case CLEAR_CONSENT_STATUS:
      return defaultStatus
    case SET_HAS_CONSENTED:
      newState = Object.assign({}, state, { hasConsented: true })
      return newState
    case USER_LOGGED_OUT:
      return defaultStatus
    default:
      return state
  }
}

const changedTempPassword = (state = false, action) => {
  switch (action.type) {
    case SET_TEMP_PASSWORD:
      return true
    default:
      return state
  }
}

export default combineReducers({
  consentForm,
  submissionConsent,
  consentStatus,
  changedTempPassword,
  v2Consents,
  completedConsents
})